import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export type DialogSize = "regular" | "large";

export enum DialogTheme {
  LIGHT = "light",
  NEWSLETTER = "newsletter",
}

type DialogContextProps = {
  dialogContents: ReactNode;
  dialogTitle: string;
  dialogSize: DialogSize;
  dialogTheme: DialogTheme;
  isDialogVisible: boolean;
  showDialog: (
    contents: ReactNode,
    title?: string,
    size?: DialogSize,
    theme?: DialogTheme
  ) => void;
  dismissDialog: () => void;
  setDialogContents: (contents: ReactNode) => void;
  setDialogTitle: (title: string) => void;
};

const initialState: DialogContextProps = {
  dialogContents: null,
  dialogTitle: "",
  dialogSize: "regular",
  dialogTheme: DialogTheme.LIGHT,
  isDialogVisible: false,
  showDialog: () => {},
  dismissDialog: () => {},
  setDialogContents: () => {},
  setDialogTitle: () => {},
};

const DialogContext = createContext<DialogContextProps>(initialState);

// Export the provider as we need to wrap the entire app with it
export function DialogProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  // Visibility
  const [isDialogVisible, setIsDialogVisible] = useState<boolean>(false);
  // Contents
  const [dialogContents, setDialogContents] = useState<ReactNode>(
    initialState.dialogContents
  );
  const [dialogTitle, setDialogTitle] = useState<string>(
    initialState.dialogTitle
  );
  const [dialogSize, setDialogSize] = useState<DialogSize>("regular");
  const [dialogTheme, setDialogTheme] = useState<DialogTheme>(
    DialogTheme.LIGHT
  );

  const dismissDialog = useCallback(() => {
    setIsDialogVisible(false);
  }, []);

  const showDialog = useCallback(
    (
      contents: ReactNode,
      title?: string,
      size?: DialogSize,
      theme?: DialogTheme
    ) => {
      const sizeToUse = size || "regular";
      setDialogSize(sizeToUse);
      setDialogTheme(theme || DialogTheme.LIGHT);
      setDialogTitle(title || "");
      setDialogContents(contents);
      setIsDialogVisible(true);
    },
    []
  );

  const memoedValue = useMemo(
    () => ({
      dialogContents,
      setDialogContents,
      dialogTitle,
      dialogSize,
      dialogTheme,
      setDialogTitle,
      showDialog,
      dismissDialog,
      isDialogVisible,
    }),
    [isDialogVisible, dialogContents]
  );

  // Only render the underlying app after we assert for the presence of a current user.
  return (
    <DialogContext.Provider value={memoedValue}>
      {children}
    </DialogContext.Provider>
  );
}

export default function useDialog() {
  return useContext(DialogContext);
}
